/**
 * AEOS control-plane panels.
 *
 * The one thing an operator must not misread here is scope. AEOS keys module settings
 * as `MODULESETTINGS#{module_id}` and the invoice policy as `INVOICEPOLICY#tenant` —
 * single rows with no account dimension. Pausing a module is a kill switch for every
 * customer, not a per-customer toggle. So the server's own `globalScopeWarning` leads
 * the tab, and every global row carries an explicit badge saying who it affects.
 */
import type { ReactElement } from 'react';
import {
  Alert,
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';

import {
  AeosCoverageNotice,
  AeosInvalidItemsAlert,
  AeosScopeBadge,
} from './AeosEstatePanels';
import type {
  AeosAccountPolicyRow,
  AeosControlPlaneSummary,
  AeosCustomerAccountRow,
  AeosInvoicePolicyView,
  AeosModuleRow,
} from '../../api/aeos';

function formatInstant(value: string | undefined): string {
  return value === undefined || value.trim().length === 0 ? '—' : value;
}

function formatCents(value: number | undefined): string {
  return value === undefined ? 'inherits the global policy' : `${value} cents`;
}

function formatDays(value: number | undefined): string {
  return value === undefined ? 'inherits the global policy' : `${value} days`;
}

interface AeosControlPlanePanelProps {
  isLoading: boolean;
  summary: AeosControlPlaneSummary | undefined;
}

export function AeosControlPlanePanel({
  isLoading,
  summary,
}: AeosControlPlanePanelProps): ReactElement {
  return (
    <SpaceBetween size="l">
      <Alert type="warning" header="Global blast radius — read scope before acting">
        {summary?.globalScopeWarning ??
          'Module settings and the invoice policy are single global rows: one row governs every account.'}
      </Alert>
      <AeosInvalidItemsAlert
        invalidItemCount={summary?.invalidItemCount ?? 0}
        subject="control-plane"
      />
      {summary ? (
        <AeosCoverageNotice coverage={summary.coverage} surfaceLabel="control plane" />
      ) : null}
      <ModuleTable isLoading={isLoading} modules={summary?.modules ?? []} />
      <InvoicePolicyPanel policy={summary?.invoicePolicy} />
      <AccountPolicyTable isLoading={isLoading} policies={summary?.accountPolicies ?? []} />
      <CustomerAccountTable isLoading={isLoading} accounts={summary?.customerAccounts ?? []} />
    </SpaceBetween>
  );
}

interface ModuleTableProps {
  isLoading: boolean;
  modules: AeosModuleRow[];
}

function ModuleTable({ isLoading, modules }: ModuleTableProps): ReactElement {
  return (
    <Table
      variant="container"
      loading={isLoading}
      loadingText="Reading AEOS module settings"
      items={modules}
      trackBy="moduleId"
      header={
        <Header
          variant="h2"
          counter={`(${modules.length})`}
          description="One settings row per module governs every account. Heartbeats record only the most recent execution, not a history."
        >
          Modules
        </Header>
      }
      empty={<Box textAlign="center">No module settings or heartbeats were returned.</Box>}
      columnDefinitions={[
        { id: 'moduleId', header: 'Module', cell: (module) => <code>{module.moduleId}</code> },
        { id: 'scope', header: 'Scope', cell: (module) => <AeosScopeBadge scope={module.scope} /> },
        {
          id: 'enabled',
          header: 'Enabled',
          cell: (module) =>
            module.enabled === undefined ? (
              <StatusIndicator type="warning">
                no setting row — the AEOS gate fails closed, so the module is stopped
              </StatusIndicator>
            ) : (
              <StatusIndicator type={module.enabled ? 'success' : 'stopped'}>
                {module.enabled ? 'enabled' : 'paused for every account'}
              </StatusIndicator>
            ),
        },
        {
          id: 'pause',
          header: 'Pause metadata',
          cell: (module) =>
            module.pausedBy === undefined && module.pauseReason === undefined
              ? '—'
              : `${module.pausedBy ?? 'unknown actor'} at ${formatInstant(module.pausedAt)}: ${module.pauseReason ?? 'no reason recorded'}`,
        },
        {
          id: 'heartbeat',
          header: 'Latest heartbeat',
          cell: (module) => <HeartbeatCell module={module} />,
        },
        {
          id: 'updated',
          header: 'Settings updated',
          cell: (module) => formatInstant(module.settingsUpdatedAt),
        },
      ]}
    />
  );
}

interface HeartbeatCellProps {
  module: AeosModuleRow;
}

function HeartbeatCell({ module }: HeartbeatCellProps): ReactElement {
  if (!module.heartbeatPresent) {
    return <StatusIndicator type="warning">no heartbeat recorded</StatusIndicator>;
  }
  const outcome = module.lastRunSkipped === true ? 'skipped' : module.lastRunOk === true ? 'ok' : 'not ok';
  return (
    <SpaceBetween size="xxs">
      <StatusIndicator
        type={module.lastRunSkipped === true ? 'info' : module.lastRunOk === true ? 'success' : 'error'}
      >
        {outcome}
      </StatusIndicator>
      <Box variant="small">
        ran at {formatInstant(module.lastRanAt)} — {module.lastRunSummary ?? 'no summary'}
      </Box>
    </SpaceBetween>
  );
}

interface InvoicePolicyPanelProps {
  policy: AeosInvoicePolicyView | undefined;
}

function InvoicePolicyPanel({ policy }: InvoicePolicyPanelProps): ReactElement {
  return (
    <Container
      header={
        <Header
          variant="h2"
          description="A single INVOICEPOLICY#tenant row. There is no per-account override for these thresholds; account policies below are the only per-account axis."
          actions={<AeosScopeBadge scope="global" />}
        >
          Invoice reconciliation policy
        </Header>
      }
    >
      {policy === undefined || !policy.present ? (
        <Alert type="warning" header="No invoice policy row is present">
          AEOS holds no <code>INVOICEPOLICY#tenant</code> row, so reconciliation runs on
          whatever defaults AEOS compiles in. The thresholds in force are not readable here.
        </Alert>
      ) : (
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Overdue critical</Box>
            <Box>{formatDays(policy.overdueCriticalDays)}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Unattended grace</Box>
            <Box>{formatDays(policy.unattendedGraceDays)}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Material gap</Box>
            <Box>{formatCents(policy.materialGapCents)}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Updated at</Box>
            <Box>{formatInstant(policy.updatedAt)}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Billable phases</Box>
            <Box>
              {policy.billablePhases === undefined || policy.billablePhases.length === 0
                ? 'none recorded — account.underbilled cannot fire'
                : policy.billablePhases.join(', ')}
            </Box>
          </div>
        </ColumnLayout>
      )}
    </Container>
  );
}

interface AccountPolicyTableProps {
  isLoading: boolean;
  policies: AeosAccountPolicyRow[];
}

function AccountPolicyTable({ isLoading, policies }: AccountPolicyTableProps): ReactElement {
  return (
    <Table
      variant="container"
      loading={isLoading}
      loadingText="Reading AEOS account policies"
      items={policies}
      trackBy="accountId"
      header={
        <Header
          variant="h2"
          counter={`(${policies.length})`}
          description="The only per-account axis in the AEOS control plane. An absent threshold inherits the global policy; absent is not zero."
        >
          Account reconciliation policies
        </Header>
      }
      empty={<Box textAlign="center">No per-account policies were returned.</Box>}
      columnDefinitions={[
        { id: 'accountId', header: 'Account', cell: (policy) => <code>{policy.accountId}</code> },
        { id: 'scope', header: 'Scope', cell: (policy) => <AeosScopeBadge scope={policy.scope} /> },
        {
          id: 'exempt',
          header: 'Reconciliation',
          cell: (policy) =>
            policy.reconciliationExempt === true ? (
              <StatusIndicator type="warning">exempt</StatusIndicator>
            ) : (
              <StatusIndicator type="success">reconciled</StatusIndicator>
            ),
        },
        {
          id: 'reason',
          header: 'Exemption reason',
          cell: (policy) => policy.exemptReason ?? '—',
        },
        {
          id: 'gap',
          header: 'Material gap',
          cell: (policy) => formatCents(policy.materialGapCents),
        },
        {
          id: 'grace',
          header: 'Unattended grace',
          cell: (policy) => formatDays(policy.unattendedGraceDays),
        },
        { id: 'updated', header: 'Updated at', cell: (policy) => formatInstant(policy.updatedAt) },
      ]}
    />
  );
}

interface CustomerAccountTableProps {
  isLoading: boolean;
  accounts: AeosCustomerAccountRow[];
}

function CustomerAccountTable({ isLoading, accounts }: CustomerAccountTableProps): ReactElement {
  return (
    <Table
      variant="container"
      loading={isLoading}
      loadingText="Reading AEOS customer accounts"
      items={accounts}
      trackBy="accountId"
      header={
        <Header
          variant="h2"
          counter={`(${accounts.length})`}
          description="Read from one platform tenant partition. A second tenant partition would be invisible here."
        >
          Customer accounts
        </Header>
      }
      empty={<Box textAlign="center">No customer accounts were returned.</Box>}
      columnDefinitions={[
        { id: 'accountId', header: 'Account', cell: (account) => <code>{account.accountId}</code> },
        { id: 'company', header: 'Company', cell: (account) => account.companyName ?? '—' },
        {
          id: 'csManaged',
          header: 'CS managed',
          cell: (account) =>
            account.csManaged === undefined ? 'unknown' : account.csManaged ? 'yes' : 'no',
        },
        {
          id: 'prospectDocs',
          header: 'Prospect doc access',
          cell: (account) =>
            account.prospectDocAccess === undefined
              ? 'unknown'
              : account.prospectDocAccess
                ? 'yes'
                : 'no',
        },
        {
          id: 'awsAccounts',
          header: 'Claimed AWS accounts',
          cell: (account) =>
            account.awsAccountIds.length === 0 ? '—' : account.awsAccountIds.join(', '),
        },
        {
          id: 'policy',
          header: 'Account policy',
          cell: (account) => (
            <StatusIndicator type={account.policyPresent ? 'success' : 'info'}>
              {account.policyPresent ? 'present' : 'none — inherits the global policy'}
            </StatusIndicator>
          ),
        },
      ]}
    />
  );
}
